export const bookingSystem = {
  seo: {
    title: "Booking — Bespire AI assistant",
    description:
      "Online booking, calendar, reminders, group classes — one capability of the Bespire AI assistant, not a separate product.",
  },
  hero: {
    badge: "Booking",
    title: "Booking that runs your calendar for you",
    subtitle:
      "The client books, the Bespire AI confirms, reminds, reschedules, follows up on no-shows. You just show up for the sessions.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    bookingCard: {
      label: "Booking view · preview",
      businessName: "Bella Studio · Prenatal yoga",
      day: "Thursday · Sep 12",
      slots: [
        { time: "4:00 pm", state: "free" as const, label: "Free" },
        { time: "5:00 pm", state: "selected" as const, label: "Selected" },
        { time: "6:00 pm", state: "booked" as const, label: "Booked" },
        { time: "7:00 pm", state: "free" as const, label: "Free" },
      ],
      confirmLabel: "Confirmed",
      confirmMessage: "See you at 5:00 pm. Reminder 2 hours before.",
      cta: "Confirm booking",
    },
  },
  process: {
    title: "Three steps to a self-running calendar",
    subtitle:
      "Same assistant that handles your website, marketing and customer messages — now also runs your calendar.",
    steps: [
      {
        step: "01",
        icon: "Calendar",
        title: "Set it up once",
        description:
          "Services, durations, prices, breaks, working hours. The Bespire AI walks you through in chat — done in 10 minutes.",
        bullets: [
          "1:1 and group sessions",
          "Buffers between bookings",
          "Multiple locations, multiple providers",
        ],
      },
      {
        step: "02",
        icon: "Bot",
        title: "The assistant runs it",
        description:
          "Clients book, pay, get confirmation. No double bookings, waitlists handled, group class capacity always visible.",
        bullets: [
          "Online payment or pay on site",
          "Auto email + SMS/WhatsApp",
          "Calendar sync with Google/Apple/Outlook",
        ],
      },
      {
        step: "03",
        icon: "Bell",
        title: "It keeps watching",
        description:
          "Reminds, reschedules, chases no-shows, asks for reviews. No-show rate drops noticeably.",
        bullets: [
          "24-hour and 2-hour reminders",
          "One-tap rescheduling",
          "Auto review request after the visit",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "Before / after: same week",
    subtitle:
      "Not more hours in the day — less idle time and fewer no-shows.",
    before: {
      label: "Before",
      businessName: "Manual calendar + DMs",
      tagline: "Booking on Messenger, email tag, paper calendar.",
      bio: "30–40 minutes a day of back-and-forth. You occasionally miss slots and sometimes double-book. You send reminders yourself, if you have time.",
      problems: [
        "Booking chats on Messenger, email, phone",
        "Conflicts and double bookings",
        "Reminders sent manually",
        "No-shows never followed up",
      ],
    },
    after: {
      label: "After",
      businessName: "Booking from Bespire",
      tagline: "One calendar, live on every channel.",
      bio: "Clients book on the site, the system confirms, reminds and reschedules. You just show up — or tell Bespire what to change.",
      wins: [
        "One calendar, live everywhere",
        "Auto confirmation + reminders",
        "No-shows chased automatically",
        "30+ minutes a day freed up",
      ],
    },
  },
  showcase: {
    title: "One day in your calendar",
    subtitle:
      "A real example. The assistant handles the grey work around every booking.",
    day: [
      { time: "07:30", icon: "Sunrise", title: "Daily brief", note: "8 confirmed bookings, 1 waitlisted. Your 10:00 is a new client — facial treatment.", tag: "brief" },
      { time: "09:00", icon: "Users", title: "Group yoga (7/8)", note: "Bespire moves Anna from the waitlist into the last spot automatically.", tag: "group" },
      { time: "10:00", icon: "Calendar", title: "New client — Classic facial", note: "Paid up front. Intake form and allergy questionnaire checked.", tag: "1:1" },
      { time: "12:30", icon: "AlertTriangle", title: "Cancellation from Tímea", note: "Bespire offered the slot to the waitlist. Filled within 3 minutes.", tag: "waitlist" },
      { time: "14:00", icon: "Bell", title: "Reminders for the 16:00 class", note: "6 clients on WhatsApp + email. 1 asked to reschedule — done.", tag: "reminder" },
      { time: "18:30", icon: "Star", title: "Wrap up the day", note: "5 new bookings, 0 conflicts, 0 no-shows. 9 sessions on the books for tomorrow.", tag: "summary" },
    ],
    kpis: [
      { label: "Daily coordination time", value: "0 min", note: "from ~30–40 min" },
      { label: "No-show rate", value: "−58%", note: "3-month average" },
      { label: "Utilization", value: "+22%", note: "thanks to waitlist + reminders" },
    ],
  },
  chat: {
    title: "Talk to your calendar like an assistant",
    subtitle:
      "Except it's your assistant handling your calendar — not you handling someone else's.",
    chat: {
      title: "Booking assistant",
      online: "Online",
      messages: [
        { role: "user" as const, content: "I can't come in tomorrow 10 to 14, doctor's appointment." },
        { role: "assistant" as const, content: "Got it, blocking that window. You have 4 bookings in that time:", list: [
          "10:00 · Anna — Classic facial",
          "11:30 · Béla — Consultation",
          "12:30 · Klára — Deep cleanse",
          "13:30 · Réka — First visit",
        ] },
        { role: "user" as const, content: "Message them, offer new times same week." },
        { role: "assistant" as const, content: "All 4 got WhatsApp + email with 2 alternatives each. I'll update you as they reply. 💜" },
        { role: "user" as const, content: "Thanks. I want to open an extra group class on the weekend." },
        { role: "assistant" as const, content: "Done: Saturday 10:00, 8-spot prenatal yoga, live on your site, draft Instagram post ready. Preview?" },
      ],
    },
  },
  crossLinks: {
    title: "Booking doesn't stop here",
    subtitle: "The Bespire AI carries the same client through reminders, marketing, review requests and your daily rhythm.",
  },
  cta: {
    title: "Automate booking today",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
